// Screen · ONE LESSON SESSION, END TO END (JOB-W02, launch-plan order 6). Route `/lesson/:id`.
//
// A teacher opens any Term 1 session of any of their classes and sees, on one page:
//   · what this session is       — class, topic, week, curriculum step, period
//   · the plan                   — its status, and a link to it (Screen 6 still owns the plan)
//   · the activities chosen FOR THIS SESSION, with the full history of what changed and when
//   · the papers prepared FOR THIS SESSION, and what each one is actually made of
//   · the teaching record        — JOB-W01's panel, mounted verbatim
//
// The HOD (Lead) opens the SAME page, read-only: every fact, no controls. Another teacher is
// refused by the API. Capabilities come from the server; this file never decides who may act.
//
// Five facts, never substituted for each other: planned · chosen · prepared · submitted/approved ·
// taught. Choosing an activity is not teaching it. Saving a paper is not teaching it.

import { api } from "../api";
import { mountMaterialActions } from '../materials-engine';
import { esc } from "../dom";
import { navigate } from "../router";
import { t } from "../i18n";
import { breadcrumbs, contextBar } from "../components/nav";
import { resourcesFor, type Resource } from "../materials";
import { loadItemBank } from "../itembank";
import { mountTeachingPanel } from "./plan-view";
import { mountDeliveryReportPanel } from "./delivery-report-panel";
import type { GuideEntry } from "../../functions/api/_lib/material-guides";

type ChoiceAction = "select" | "unselect";
type ResourceKind = "pack" | "sheet" | "reference" | "practice" | "audio" | "itembank";

interface CurrentChoice {
  resource_key: string;
  resource_kind: ResourceKind;
  resource_title: string;
  card_id: string | null;
  card_version_sha256: string | null;
  chosen_at: string;
  revision: number;
  changes: number;
}
interface ChoiceHistoryRow {
  revision: number;
  action: ChoiceAction;
  resource_key: string;
  resource_kind: ResourceKind;
  resource_title: string;
  card_id: string | null;
  card_version_short: string | null;
  actor_name: string | null;
  recorded_at: string;
}
interface PaperCardRef {
  card_id: string;
  card_version_short: string;
  origin: "exact_card" | "edited_after_import";
}
interface SessionPaper {
  id: string;
  kind: "test" | "worksheet";
  title: string;
  status: "draft" | "tos_ok" | "checked";
  checked_at: string | null;
  created_at: string;
  updated_at: string;
  total_marks: number;
  block_count: number;
  source: { resource_key: string; resource_kind: string | null; resource_title: string | null } | null;
  provenance: { exact_card: number; edited_after_import: number; teacher_written: number; cards: PaperCardRef[] };
}
interface SessionMaterialWorksheet {
  id: string;
  title: string;
  node_id: string;
  revision: number;
  source_version: string;
  activity_count: number;
  key_ready: boolean;
  updated_at: string;
  can_edit: boolean;
}
interface SessionResponse {
  lesson: {
    id: string; topic: string; class_id: string; class_name: string; class_level: string | null;
    teacher_id: string; term_id: string; week_no: number; node_id: string | null;
    strand: string | null; slot: number | null; duration_min: number;
    plan_status: "not_started" | "in_progress" | "submitted" | "approved";
    plan_id: string | null; approval_note: string | null;
  };
  curriculum_link: "node" | "freeform";
  teaching: { state: "not_recorded" | "taught" | "confirmation_withdrawn"; revision: number; confirmed_at: string | null; withdrawn_at: string | null };
  choices: { revision: number; current: CurrentChoice[]; history: ChoiceHistoryRow[] };
  standing_preference: string | null;
  papers: SessionPaper[];
  source_material: { node_id: string; title: string; source_version: string } | null;
  readable_guide: GuideEntry | null;
  material_worksheets: SessionMaterialWorksheet[];
  capabilities: { can_choose: boolean; can_create_paper: boolean; read_only_reason: "not_owner" | null };
}

const STRAND_LABEL: Record<string, string> = { coursebook: "Coursebook", thread: "Thread", fluency: "Fluency" };
const PLAN_STATUS_KEY = {
  not_started: "sess.plan_status_not_started",
  in_progress: "sess.plan_status_in_progress",
  submitted: "sess.plan_status_submitted",
  approved: "sess.plan_status_approved",
} as const;

/** recorded_at is a UTC SQLite datetime ("YYYY-MM-DD HH:MM:SS"); show it on the school's clock.
 *  Same conversion the teaching panel uses — a session's facts must all read on one clock. */
function schoolTime(utc: string | null): string {
  if (!utc) return "";
  const d = new Date(`${utc.replace(" ", "T")}${/[zZ]|[+-]\d\d:?\d\d$/.test(utc) ? "" : "Z"}`);
  if (Number.isNaN(d.getTime())) return utc;
  return d.toLocaleString("en-GB", {
    timeZone: "Asia/Phnom_Penh", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}
function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function resourceIcon(kind: ResourceKind | string): string {
  if (kind === "audio") return "🔊";
  if (kind === "practice" || kind === "itembank") return "🧩";
  if (kind === "sheet") return "📝";
  if (kind === "pack") return "📄";
  return "📖";
}

/** An item-bank resource key is `itembank:<card id>` — the one resource kind that carries an exact
 *  version, and the only one the database insists on a card identity for. */
const cardIdFromKey = (key: string): string | null => (key.startsWith("itembank:") ? key.slice(9) || null : null);

function teachingChip(state: SessionResponse["teaching"]["state"]): string {
  if (state === "taught") return `<span class="chip taught">${t("teach.state_taught")}</span>`;
  if (state === "confirmation_withdrawn") return `<span class="chip withdrawn">${t("teach.state_withdrawn")}</span>`;
  return `<span class="chip">${t("teach.state_not_recorded")}</span>`;
}
function planChip(status: SessionResponse["lesson"]["plan_status"]): string {
  const cls = status === "approved" ? " approved" : status === "submitted" ? " submitted" : "";
  return `<span class="chip${cls}">${t(PLAN_STATUS_KEY[status])}</span>`;
}

export async function renderLessonView(app: HTMLElement, params: Record<string, string>): Promise<void> {
  const lessonId = params.id!;
  const url = `/api/lessons/${encodeURIComponent(lessonId)}/session`;
  let data: SessionResponse;
  try {
    data = await api<SessionResponse>(url);
  } catch (error) {
    // A 403/404 is a real answer, not an outage — say so on a page that still has a way out.
    app.innerHTML = `
      <div class="topbar"><nav class="breadcrumb" aria-label="Breadcrumb"><a data-link href="/plans">My plans</a><span class="sep" aria-hidden="true">›</span><span class="current" aria-current="page">${t("sess.title")}</span></nav></div>
      <h1>${t("sess.title")}</h1>
      <p class="error" data-session-error>${esc((error as Error).message || t("sess.load_error"))}</p>`;
    return;
  }

  // The node's own ranked resource list — what there IS to choose from. A node with no citations
  // (150 nodes fleet-wide have none) simply offers nothing; that is a calm empty state, not an error.
  // Packs and sheets stay in: for this session they are as choosable as any other resource.
  let offered: Resource[] = [];
  try {
    offered = (await resourcesFor(data.lesson.node_id))?.items ?? [];
  } catch {
    offered = []; // the index is a nicety here — the recorded history is the page's real content
  }
  // Exact card versions for the item-bank rows, so a choice records WHICH version was chosen.
  // A card that has since left the bank is simply not offered; one already chosen still shows,
  // because the history keeps its own copy of the id and version.
  const cardVersions = new Map<string, string>();
  if (offered.some((r) => r.key.startsWith("itembank:"))) {
    try {
      for (const card of (await loadItemBank())?.cards ?? []) cardVersions.set(card.id, card.card_version_sha256);
    } catch {
      /* no bank → item-bank rows are not offerable this render; nothing already recorded is lost */
    }
  }

  let pending: { action: ChoiceAction; key: string; idem: string } | null = null;
  let showAllOffered = false;
  let message = "";
  let error = "";
  let deliveryRevision: number | null = null;

  /** Choice POST responses intentionally keep the established session-trail contract. Merge them
   *  over the richer GET response so both success and conflict refreshes retain the read-only
   *  material facts. An explicitly returned null still wins; only an absent field is retained. */
  function mergeSession(next: Partial<SessionResponse>): SessionResponse {
    const merged = { ...data, ...next } as SessionResponse;
    if (!Object.prototype.hasOwnProperty.call(next, "source_material")) merged.source_material = data.source_material;
    if (!Object.prototype.hasOwnProperty.call(next, "readable_guide")) merged.readable_guide = data.readable_guide;
    if (!Object.prototype.hasOwnProperty.call(next, "material_worksheets")) merged.material_worksheets = data.material_worksheets;
    return merged;
  }

  const owner = data.capabilities.can_choose;
  const chosenKeys = (): Set<string> => new Set(data.choices.current.map((c) => c.resource_key));

  // ── the session's own identity ────────────────────────────────────────────────────────────────
  function headerHtml(): string {
    const l = data.lesson;
    const period = l.strand && l.slot
      ? ` · ${t("sess.period", { strand: STRAND_LABEL[l.strand] ?? l.strand, n: l.slot })}`
      : l.strand
        ? ` · ${STRAND_LABEL[l.strand] ?? l.strand}`
        : "";
    const step = l.node_id ? ` · ${t("sess.step")} ${esc(l.node_id)}` : "";
    return `
      <h1>${esc(l.topic)}</h1>
      <p class="muted" data-session-context>${esc(l.class_name)} · ${t("common.week_n", { n: l.week_no })}${step}${period}</p>
      <div class="sess-facts" data-session-facts>${planChip(l.plan_status)} ${teachingChip(data.teaching.state)}</div>
      ${data.curriculum_link === "freeform" ? `<p class="hint" data-session-freeform>${t("sess.freeform")}</p>` : ""}
      ${owner ? "" : `<p class="hint" data-session-readonly>${t("sess.read_only")}</p>`}`;
  }

  function planHtml(): string {
    const l = data.lesson;
    const link = l.plan_id
      ? `<a class="linkbtn" data-link data-session-plan-link href="/plan/${esc(l.plan_id)}?term_id=${encodeURIComponent(l.term_id)}">${t("sess.plan_open")}</a>`
      : owner
        ? `<a class="linkbtn" data-link data-session-plan-link href="/lesson/${esc(l.id)}/edit">${t("sess.plan_start")}</a>`
        : `<p class="hint">${t("sess.plan_none")}</p>`;
    return `<section class="card" id="session-plan">
      <div class="eyebrow">${t("sess.plan_title")}</div>
      <div class="sess-facts">${planChip(l.plan_status)}</div>
      ${link}
    </section>`;
  }

  function journeyHtml(): string {
    const guide = data.readable_guide;
    const readable = !!guide && ["complete_guide", "activities_only"].includes(guide.state);
    const prepared = data.papers.length + data.material_worksheets.length;
    const taught = data.teaching.state === "taught";
    const sourceQuery = new URLSearchParams({ class: data.lesson.class_id, lesson: data.lesson.id, term: data.lesson.term_id, week: String(data.lesson.week_no) });
    const sourceDetail = readable && guide
      ? `<a data-link href="/materials-guide/${encodeURIComponent(guide.node_id)}?${esc(sourceQuery.toString())}">${esc(guide.title ?? "Readable lesson guide")}</a>${guide.source_version ? ` · source ${esc(guide.source_version.slice(0, 8))}` : ""}<br />${guide.state === "complete_guide" ? "Full teacher guide" : "Activities only"} · Worksheet studio: ${guide.worksheet_available ? "available" : "unavailable"} · Guide activity import: ${guide.import_enabled ? "available" : "unavailable"}`
      : data.lesson.node_id ? `${esc(guide?.reason ?? "A readable HTML guide is unavailable for this step.")} Open the original lesson resources below.` : "This freeform lesson has no linked curriculum source.";
    const leadDetail = owner
      ? "The Lead sees saved preparation, delivery reports and whole-session confirmation as separate facts."
      : `<a data-link href="/lead/teaching?term_id=${encodeURIComponent(data.lesson.term_id)}&class_id=${encodeURIComponent(data.lesson.class_id)}">Open private teaching history</a> · <a data-link href="/lead/progress?term_id=${encodeURIComponent(data.lesson.term_id)}">Open Lead Progress</a>`;
    const step = (number: number, label: string, detail: string, ready: boolean, key: string): string => `
      <li data-journey-step="${key}" data-ready="${ready}">
        <span class="chip${ready ? " approved" : ""}">${number}. ${esc(label)}</span>
        <span class="res-detail">${detail}</span>
      </li>`;
    return `<section class="card" id="session-journey" data-lesson-journey>
      <h2 class="sess-title">Lesson journey</h2>
      <p class="hint">Source, preparation and teaching are saved as separate facts.</p>
      <ol>
        ${step(1, readable ? guide?.state === "complete_guide" ? "Full guide ready" : "Activities readable" : "Open original sources", sourceDetail, readable, "source")}
        ${step(2, prepared ? "Paper prepared" : "Paper not prepared", prepared ? `${prepared} saved for this lesson` : "No worksheet or test is linked yet.", prepared > 0, "paper")}
        ${step(3, deliveryRevision === null ? "Read teaching update" : deliveryRevision ? "Content reported" : "No teaching update recorded", deliveryRevision === null ? "Report status is loading separately below." : deliveryRevision ? `${deliveryRevision} retained report${deliveryRevision === 1 ? "" : "s"}. Whole-session confirmation: ${taught ? "confirmed" : data.teaching.state === "confirmation_withdrawn" ? "withdrawn" : "not recorded"}.` : taught ? "Exact content not recorded. A separate whole-session confirmation exists." : "Use After the lesson to report actual content and used worksheets.", !!deliveryRevision, "teaching")}
        ${step(4, "Visible to Lead", leadDetail, prepared > 0 || data.teaching.revision > 0 || !!deliveryRevision, "lead")}
      </ol>
    </section>`;
  }

  // ── activities ────────────────────────────────────────────────────────────────────────────────
  function chosenRowHtml(c: CurrentChoice): string {
    const currentResource = offered.find((r) => r.key === c.resource_key);
    const title = currentResource?.href
      ? `<a class="res-title" href="${esc(currentResource.href)}" target="_blank" rel="noopener">${esc(c.resource_title)}</a>`
      : `<span class="res-title">${esc(c.resource_title)}</span>`;
    const card = c.card_id && c.card_version_sha256
      ? `<span class="res-detail" data-choice-card="${esc(c.card_id)}">${t("sess.act_card", { id: c.card_id, short: c.card_version_sha256.slice(0, 8) })}</span>`
      : "";
    const changed = c.changes > 1 ? ` · ${t("sess.act_changed_n", { n: c.changes })}` : "";
    // Only a citation-only choice gets its own "make a worksheet from this" — an item-bank card
    // records its exact card and version instead (F-2026-09-17-15). Its own label, never the
    // section button's: three links reading "Make a worksheet for this lesson" on one page is not
    // a choice, it is a guess. This one says which activity it starts from.
    const fromThis = owner && c.resource_kind !== "itembank"
      ? `<a class="linkbtn quiet" href="#" data-choice-paper="${esc(c.resource_key)}">${t("sess.paper_new_from_this")}</a>`
      : "";
    return `<div class="res-row res-used" data-choice-current="${esc(c.resource_key)}">
      <span class="res-icon">${resourceIcon(c.resource_kind)}</span>
      <div class="res-body">
        ${title}
        ${card}
        <span class="res-detail">${t("sess.act_chosen_on", { when: schoolTime(c.chosen_at) })}${changed}</span>
        ${fromThis}
      </div>
      ${owner
        ? `<button type="button" class="res-tick active" data-choice-act="unselect" data-choice-key="${esc(c.resource_key)}">${t("sess.act_remove")}</button>`
        : `<span class="chip" data-choice-readonly>${t("sess.act_chosen")}</span>`}
    </div>`;
  }

  function offerRowHtml(r: Resource, hidden: boolean): string {
    const cardId = cardIdFromKey(r.key);
    // An item-bank row is only offerable when its exact version can be read right now: a choice
    // without its version would be a provenance hole, and the API refuses it anyway.
    if (cardId && !cardVersions.has(cardId)) return "";
    const title = r.href
      ? `<a class="res-title" href="${esc(r.href)}" target="_blank" rel="noopener">${esc(r.title)}</a>`
      : `<span class="res-title">${esc(r.title)}</span>`;
    return `<div class="res-row" data-choice-offer="${esc(r.key)}"${hidden ? " hidden" : ""}>
      <span class="res-icon">${resourceIcon(cardId ? "itembank" : r.kind)}</span>
      <div class="res-body">
        ${title}
        ${r.detail ? `<span class="res-detail">${esc(r.detail)}</span>` : ""}
        ${r.permission ? `<span class="res-permission">${esc(r.permission)}</span>` : ""}
      </div>
      <button type="button" class="res-tick" data-choice-act="select" data-choice-key="${esc(r.key)}">${t("sess.act_choose")}</button>
    </div>`;
  }

  function standingHtml(): string {
    const key = data.standing_preference;
    if (!owner || !key || chosenKeys().has(key)) return "";
    const known = offered.find((r) => r.key === key);
    // A standing pick whose resource has left the index can't be offered by title — stay silent
    // rather than showing a raw key. It is a suggestion, not a fact about this session.
    if (!known) return "";
    return `<p class="hint" data-choice-standing="${esc(key)}">${t("sess.standing", { title: known.title })}
      <button type="button" class="linkbtn" data-choice-act="select" data-choice-key="${esc(key)}">${t("sess.standing_use")}</button>
      <br /><span class="muted">${t("sess.standing_hint")}</span></p>`;
  }

  function choiceHistoryHtml(): string {
    const rows = data.choices.history;
    if (!rows.length) return `<p class="hint" data-choice-history-empty>${t("sess.act_history_empty")}</p>`;
    const items = rows
      .map((e) => {
        const what = e.action === "select" ? t("sess.act_event_select") : t("sess.act_event_unselect");
        const version = e.card_version_short ? ` <span class="muted">(${esc(e.card_id ?? "")} · ${esc(e.card_version_short)})</span>` : "";
        return `<li data-revision="${e.revision}"><strong>${what}</strong> ${esc(e.resource_title)}${version}
          <span class="muted">· ${esc(e.actor_name ?? "")} · ${esc(schoolTime(e.recorded_at))}</span></li>`;
      })
      .join("");
    return `<details class="teach-history sess-history" open><summary>${t("sess.act_history")} (${rows.length})</summary><ol>${items}</ol></details>`;
  }

  function activitiesHtml(): string {
    const current = data.choices.current;
    const chosen = chosenKeys();
    const rest = offered.filter((r) => !chosen.has(r.key));
    const FOLD = 5;
    const offerRows = rest.map((r, i) => offerRowHtml(r, !showAllOffered && i >= FOLD && !r.required)).join("");
    const more = !showAllOffered && rest.length > FOLD
      ? `<button type="button" class="res-showall linkbtn" data-choice-act="show-all">${t("sess.act_browse_all", { n: rest.length })}</button>`
      : "";
    const offers = owner
      ? `<div class="sess-offer">
           <div class="eyebrow">${t("sess.act_available")}</div>
           ${rest.length
             ? `<p class="hint">${t("sess.act_available_hint")}</p>${offerRows}${more}`
             : `<p class="empty" data-choice-offers-empty>${t("sess.act_available_none")}</p>`}
         </div>`
      : "";
    return `
      <h2 class="sess-title">${t("sess.act_title")}</h2>
      <p class="hint">${owner ? t("sess.act_hint") : t("sess.act_hint_reader")}</p>
      ${message ? `<p class="teach-flash" role="status">${esc(message)}</p>` : ""}
      ${error ? `<p class="error" data-choice-error>${esc(error)}</p>` : ""}
      <div data-choice-count="${current.length}">
        ${current.length
          ? current.map(chosenRowHtml).join("")
          : `<p class="empty" data-choice-empty>${owner ? t("sess.act_none") : t("sess.act_none_reader")}</p>`}
      </div>
      ${standingHtml()}
      ${offers}
      ${choiceHistoryHtml()}`;
  }

  // ── papers ────────────────────────────────────────────────────────────────────────────────────
  function provenanceHtml(p: SessionPaper): string {
    const v = p.provenance;
    if (!p.block_count) return `<span class="res-detail">${t("sess.paper_empty_blocks")}</span>`;
    const parts = [
      v.exact_card ? t("sess.paper_exact", { n: v.exact_card }) : "",
      v.edited_after_import ? t("sess.paper_edited", { n: v.edited_after_import }) : "",
      v.teacher_written ? t("sess.paper_written", { n: v.teacher_written }) : "",
    ].filter(Boolean);
    const cards = v.cards.length
      ? `<span class="res-detail" data-paper-cards>${t("sess.paper_cards")}: ${v.cards
          .map((c) => `${esc(c.card_id)} · ${esc(c.card_version_short)}${c.origin === "edited_after_import" ? " ✎" : ""}`)
          .join(", ")}</span>`
      : "";
    return `<span class="res-detail" data-paper-provenance>${parts.join(" · ")}</span>${cards}`;
  }

  function paperRowHtml(p: SessionPaper): string {
    const from = p.source
      ? `<span class="res-detail" data-paper-source="${esc(p.source.resource_key)}">${t("sess.paper_from", { title: p.source.resource_title ?? p.source.resource_key })}</span>`
      : "";
    return `<div class="card sess-paper" data-paper="${esc(p.id)}">
      <div class="sess-paper-head">
        <strong>${esc(p.title)}</strong>
        <span class="chip${p.status === "checked" ? " approved" : ""}">${p.kind === "test" ? "Test" : "Worksheet"}</span>
      </div>
      <div class="res-body">
        <span class="res-detail">${p.total_marks === 1 ? t("sess.paper_marks_one") : t("sess.paper_marks", { n: p.total_marks })} · ${p.block_count === 1 ? t("sess.paper_blocks_one") : t("sess.paper_blocks", { n: p.block_count })}</span>
        ${from}
        ${provenanceHtml(p)}
      </div>
      <div class="row-actions">
        <a class="linkbtn" data-link href="/tests/${esc(p.id)}">${t("sess.paper_open")}</a>
        <a class="linkbtn" data-link href="/tests/${esc(p.id)}/print">${t("sess.paper_print")}</a>
      </div>
    </div>`;
  }

  function materialWorksheetRowHtml(p: SessionMaterialWorksheet): string {
    const query = new URLSearchParams({ worksheet: p.id, class: data.lesson.class_id, lesson: data.lesson.id, term: data.lesson.term_id, week: String(data.lesson.week_no) });
    return `<div class="card sess-paper" data-material-worksheet="${esc(p.id)}">
      <div class="sess-paper-head">
        <strong>${esc(p.title)}</strong>
        <span class="chip${p.key_ready ? " approved" : ""}">Source worksheet</span>
      </div>
      <div class="res-body">
        <span class="res-detail">${p.activity_count} ${p.activity_count === 1 ? "activity" : "activities"} · revision ${p.revision} · source ${esc(p.source_version.slice(0, 8))}</span>
        <span class="res-detail" data-material-key-ready="${p.key_ready}">${p.key_ready ? "Teacher key ready" : "Teacher key needs review"}</span>
      </div>
      <div class="row-actions">
        <a class="linkbtn" data-link href="/materials-studio/${encodeURIComponent(p.node_id)}?${esc(query.toString())}">${p.can_edit ? "Open source worksheet" : "View source worksheet"}</a>
      </div>
    </div>`;
  }

  function papersHtml(): string {
    const cards = data.choices.current.filter((c) => c.resource_kind === "itembank" && c.card_id);
    const only = data.choices.current.length === 1 ? data.choices.current[0]! : null;
    // F-2026-09-17-15 is RULED (Ruling A, 2026-09-18) and this now works: the cards chosen for this
    // session travel into the worksheet. Classroom use spends no M1/M2/SA1 allocation; the use is
    // still recorded, and an assembler of a monthly paper is warned if it reaches for the same
    // version in the same grade and year. The choice stays recorded here with its exact id and
    // version either way — that is the provenance an HOD follows.
    const hint = cards.length ? t("sess.paper_new_cards", { n: cards.length }) : t("sess.paper_new_blank");
    const make = data.capabilities.can_create_paper
      ? `<a class="linkbtn make-worksheet" href="#" data-paper-new${only ? ` data-paper-source="${esc(only.resource_key)}"` : ""}>${t("sess.paper_new")}</a>
         <p class="hint">${t("sess.paper_new_hint")} ${esc(hint)}</p>`
      : "";
    const savedCount = data.papers.length + data.material_worksheets.length;
    return `
      <h2 class="sess-title">${t("sess.paper_title")}</h2>
      <p class="hint">${owner ? t("sess.paper_hint") : t("sess.paper_hint_reader")}</p>
      <div data-paper-count="${savedCount}" data-classic-paper-count="${data.papers.length}" data-material-worksheet-count="${data.material_worksheets.length}">
        ${savedCount
          ? data.papers.map(paperRowHtml).join("") + data.material_worksheets.map(materialWorksheetRowHtml).join("")
          : `<p class="empty" data-paper-empty>${owner ? t("sess.paper_none") : t("sess.paper_none_reader")}</p>`}
      </div>
      ${make}`;
  }

  // ── render ────────────────────────────────────────────────────────────────────────────────────
  function paint(): void {
    const l = data.lesson;
    app.innerHTML = `
      ${breadcrumbs([
        { label: t("nav.crumb_home"), href: "/" },
        { label: l.class_name, href: `/classes/${encodeURIComponent(l.class_id)}?term_id=${encodeURIComponent(l.term_id)}` },
        { label: t("sess.crumb") },
      ])}
      ${headerHtml()}
      ${contextBar([
        { label: l.class_name, href: `/classes/${encodeURIComponent(l.class_id)}?term_id=${encodeURIComponent(l.term_id)}` },
        { label: t("common.week_n", { n: l.week_no }), href: `/classes/${encodeURIComponent(l.class_id)}?term_id=${encodeURIComponent(l.term_id)}&week=${l.week_no}` },
      ])}
      ${journeyHtml()}
      ${planHtml()}
      <section class="card" id="session-activities">${activitiesHtml()}</section>
      <section class="card" id="session-papers">${papersHtml()}</section>
      <section class="card" id="after-lesson" aria-live="polite"><h2 class="sess-title">After the lesson</h2><p class="muted">Reading teaching reports…</p></section>
      <h2 class="sess-title">Separate whole-session confirmation</h2><p class="hint">This existing confirmation records the teacher's whole-session statement. Exact content and used worksheet versions are recorded in After the lesson above.</p>
      <section class="card teach-panel" id="teaching" aria-live="polite">
        <p class="muted">${t("teach.loading")}</p>
      </section>`;
    // The teaching record is W01's panel, unchanged. Its own state is the authority; the header chip
    // is refreshed from it so a confirmation made here never leaves a stale chip above.
    void mountTeachingPanel(app.querySelector<HTMLElement>("#teaching")!, l.id, (state) => {
      data.teaching = { ...data.teaching, state };
      const facts = app.querySelector<HTMLElement>("[data-session-facts]");
      if (facts) facts.innerHTML = `${planChip(l.plan_status)} ${teachingChip(state)}`;
      const journey = app.querySelector<HTMLElement>("#session-journey");
      if (journey) journey.outerHTML = journeyHtml();
      const empty = app.querySelector<HTMLElement>("[data-delivery-empty]");
      if (empty) empty.textContent = state === "taught" ? "Exact content not recorded. A separate whole-session confirmation exists." : "No teaching update recorded.";
    });
    void mountDeliveryReportPanel(app.querySelector<HTMLElement>("#after-lesson")!, l.id, () => data.teaching.state, (reports) => {
      deliveryRevision = reports.revision;
      const journey = app.querySelector<HTMLElement>("#session-journey");
      if (journey) journey.outerHTML = journeyHtml();
    });
  }

  /** Repaint only the two sections a choice can change, so the teaching panel below is never torn
   *  down and remounted (that would drop its in-flight retry key). */
  function repaintChoices(): void {
    const box = app.querySelector<HTMLElement>("#session-activities");
    const papers = app.querySelector<HTMLElement>("#session-papers");
    if (box) box.innerHTML = activitiesHtml();
    if (papers) papers.innerHTML = papersHtml();
  }

  async function send(action: ChoiceAction, resource: { key: string; kind: ResourceKind; title: string; card_id: string | null; version: string | null }): Promise<void> {
    // A retry after a dropped connection re-sends the SAME key, so the server replays instead of
    // appending a duplicate. Any server answer — success or refusal — ends that request's key.
    if (!pending || pending.action !== action || pending.key !== resource.key) {
      pending = { action, key: resource.key, idem: newIdempotencyKey() };
    }
    const request = pending;
    app.querySelectorAll<HTMLButtonElement>("#session-activities button").forEach((b) => (b.disabled = true));
    message = "";
    error = "";
    let res: Response;
    try {
      res = await fetch(`/api/lessons/${encodeURIComponent(lessonId)}/choices`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action,
          expected_revision: data.choices.revision,
          idempotency_key: request.idem,
          resource_key: resource.key,
          resource_kind: resource.kind,
          resource_title: resource.title,
          ...(resource.card_id ? { card_id: resource.card_id, card_version_sha256: resource.version } : {}),
        }),
      });
    } catch {
      error = t("sess.act_offline"); // keep `pending` — the retry re-uses its key
      repaintChoices();
      return;
    }
    pending = null;
    const body = (await res.json().catch(() => ({}))) as Partial<SessionResponse> & { error?: string; current?: SessionResponse };
    if (res.ok && body.choices) {
      // Choice writes still return the established session-trail shape. The structured source and
      // worksheet summaries are read-only facts from the GET route, so retain them while applying
      // the new choice history rather than making the UI pretend those worksheets disappeared.
      data = mergeSession(body);
      // Say what the list now shows. A replayed request whose outcome changed elsewhere must not
      // flash "Added" beside a list that no longer contains it.
      const present = data.choices.current.some((c) => c.resource_key === resource.key);
      const expected = action === "select";
      message = present !== expected
        ? t("sess.act_replayed_other")
        : expected ? t("sess.act_saved_select") : t("sess.act_saved_unselect");
    } else {
      if (body.current) data = mergeSession(body.current); // stale: show latest choices without dropping material facts
      error = body.error ?? "Something went wrong.";
    }
    repaintChoices();
  }

  /** Open the worksheet editor for THIS session. class and node are deliberately NOT sent: the
   *  server derives them from the lesson, and sending them again would only invite a mismatch. */
  function makePaper(sourceKey: string | null): void {
    const l = data.lesson;
    const query = new URLSearchParams({
      new: "worksheet",
      lesson: l.id,
      title: `${l.node_id ?? l.class_name} · ${l.topic}`,
    });
    const source = sourceKey ? data.choices.current.find((c) => c.resource_key === sourceKey) ?? null : null;
    if (source) {
      query.set("source", source.resource_key);
      query.set("source_kind", source.resource_kind);
      query.set("source_title", source.resource_title);
    }
    // The Item Bank cards chosen for this session travel with it (ruling A). When one source
    // activity was named, only that one card is carried — otherwise the paper starts with every
    // card currently chosen for the session, in the order the teacher chose them. Grade and year
    // are NOT sent: both are derived server-side from the lesson and its term.
    const chosen = source ? [source] : data.choices.current;
    for (const cardId of new Set(chosen.filter((c) => c.resource_kind === "itembank" && c.card_id).map((c) => c.card_id!))) {
      query.append("card", cardId);
    }
    navigate(`/tests?${query.toString()}`);
  }
  if(data.lesson.node_id)void mountMaterialActions(app,data.lesson.node_id,new URLSearchParams({class:data.lesson.class_id,lesson:data.lesson.id,term:data.lesson.term_id,week:String(data.lesson.week_no)}));

  app.addEventListener("click", (e) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-choice-act], [data-paper-new], [data-choice-paper]");
    if (!el || (el as HTMLButtonElement).disabled) return;

    if (el.hasAttribute("data-paper-new")) {
      e.preventDefault();
      makePaper(el.dataset.paperSource ?? null);
      return;
    }
    if (el.hasAttribute("data-choice-paper")) {
      e.preventDefault();
      makePaper(el.dataset.choicePaper ?? null);
      return;
    }

    const act = el.dataset.choiceAct;
    if (act === "show-all") {
      showAllOffered = true;
      repaintChoices();
      return;
    }
    const key = el.dataset.choiceKey;
    if (!key || (act !== "select" && act !== "unselect")) return;
    e.preventDefault();

    if (act === "unselect") {
      const current = data.choices.current.find((c) => c.resource_key === key);
      if (!current) return;
      void send("unselect", { key, kind: current.resource_kind, title: current.resource_title, card_id: current.card_id, version: current.card_version_sha256 });
      return;
    }
    const offer = offered.find((r) => r.key === key);
    if (!offer) return;
    const cardId = cardIdFromKey(key);
    const version = cardId ? cardVersions.get(cardId) ?? null : null;
    if (cardId && !version) return; // never record an item-bank choice without its exact version
    void send("select", {
      key,
      kind: cardId ? "itembank" : (offer.kind as ResourceKind),
      title: offer.title,
      card_id: cardId,
      version,
    });
  });

  paint();
  app.addEventListener("eden:mounted", () => {
    if (location.hash === "#after-lesson") app.querySelector<HTMLElement>("#after-lesson")?.scrollIntoView({ block: "start" });
  }, { once: true });
}
